import { createContext, useContext, useEffect, useState } from 'react';
import {
    onAuthStateChanged,
    signInWithPopup,
    signInWithEmailAndPassword,
    EmailAuthProvider,
    reauthenticateWithCredential,
    updatePassword,
    signOut,
} from 'firebase/auth';
import { auth, googleProvider } from './firebase.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (u) => {
            setUser(u);
            setLoading(false);
        });
        return unsub;
    }, []);

    async function signInGoogle() {
        return signInWithPopup(auth, googleProvider);
    }

    async function signInEmail(email, password) {
        return signInWithEmailAndPassword(auth, email, password);
    }

    // 站內直接改密碼（不寄信）。Firebase 對改密碼這類敏感操作要求「剛登入過」，
    // 所以先用目前密碼做 re-auth，再 updatePassword，當場改完。
    async function changePassword(currentPassword, newPassword) {
        const u = auth.currentUser;
        if (!u || !u.email) throw new Error('not_signed_in');
        const cred = EmailAuthProvider.credential(u.email, currentPassword);
        await reauthenticateWithCredential(u, cred);
        await updatePassword(u, newPassword);
    }

    async function signOutUser() {
        // 清 hostId/cache 類 key（避免不同帳號殘留），保留「記住 email」相關
        const keepKeys = new Set(['qubby-admin:lastEmail', 'qubby-admin:rememberEmail']);
        for (let i = localStorage.length - 1; i >= 0; i--) {
            const k = localStorage.key(i);
            if (k && k.startsWith('qubby-admin:') && !keepKeys.has(k)) {
                localStorage.removeItem(k);
            }
        }
        await signOut(auth);
    }

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                signInGoogle,
                signInEmail,
                changePassword,
                signOut: signOutUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
    return ctx;
}
